import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { WidgetError } from "@/components/WidgetError";
import { PaginationBar } from "@/components/PaginationBar";
import { Download, FileClock } from "lucide-react";

export default function PortalStatements() {
  const [d, setD] = useState({ items: [], pages: 1 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api.get("/portal/finance/statements", { params: { page, limit: 20 } })
      .then(({ data }) => setD(data))
      .catch(setError)
      .finally(() => setLoading(false));
  }, [page]);
  useEffect(load, [load]);

  const download = async (sid, stNo) => {
    try {
      const res = await api.get(`/portal/finance/statements/${sid}/pdf`,
                                { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `statement-${stNo || sid}.pdf`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) {}
  };

  if (error) return <WidgetError error={error} onRetry={load}
                                 title="Could not load statements" testId="portal-stmts-error"/>;

  return (
    <div data-testid="portal-statements-page">
      <div className="flex items-center gap-2 mb-4">
        <FileClock size={20}/>
        <h1 className="text-3xl font-black tracking-tight">Statements</h1>
      </div>
      <div className="bg-white border border-slate-200 rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 label-overline text-slate-600">
            <tr>
              <th className="text-left px-3 py-2">Statement #</th>
              <th className="text-left px-3 py-2">Period</th>
              <th className="text-right px-3 py-2">Opening</th>
              <th className="text-right px-3 py-2">Closing</th>
              <th className="w-24"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="p-8 text-center text-slate-500">Loading…</td></tr>
            ) : d.items.length === 0 ? (
              <tr><td colSpan={5} className="p-8 text-center text-xs text-slate-500">No statements.</td></tr>
            ) : d.items.map((s) => (
              <tr key={s.id} className="border-b border-slate-100"
                  data-testid={`portal-stmt-row-${s.id}`}>
                <td className="px-3 py-2 font-mono text-xs">{s.statement_no || "—"}</td>
                <td className="px-3 py-2 text-xs">{s.period_start} → {s.period_end}</td>
                <td className="px-3 py-2 font-mono text-right">{fmtMoney(s.opening_balance_cents)}</td>
                <td className="px-3 py-2 font-mono text-right font-bold">{fmtMoney(s.closing_balance_cents)}</td>
                <td className="px-3 py-2">
                  <button onClick={() => download(s.id, s.statement_no)}
                          data-testid={`portal-stmt-download-${s.id}`}
                          className="text-xs font-bold text-slate-700 hover:text-slate-900 flex items-center gap-1">
                    <Download size={12}/> PDF
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PaginationBar page={page} pages={d.pages || 1} onChange={setPage}/>
    </div>
  );
}
